import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Link } from 'react-router-dom';
import {
  FaTasks,
  FaExternalLinkAlt,
  FaListUl,
  FaCircleNotch,
  FaExclamationTriangle,
  FaCheckCircle,
} from 'react-icons/fa';
import TaskService from '@frontend/AppSettings/services/TaskService';
import ClientMattersService from '@frontend/AppSettings/services/ClientMattersService';
import ClientService from '@frontend/AppSettings/services/ClientService';
import AgentsService from '@frontend/AppSettings/services/AgentsService';
import { isLegalEnvironment } from '@frontend/AppSettings/environmentConfig';
import Table from '@frontend/Components/Table/Table';
import FilterDropdown from '@frontend/Components/FilterDropdown/FilterDropdown';
import type { Column } from '@frontend/Models/Table/TableModels';
import StatusBadge from '@frontend/Components/StatusBadge/StatusBadge';
import './StakeholderActionItems.css';

interface StakeholderActionItemsProps {
  stakeholderId: string;
  type: 'individual' | 'organization';
}

const StakeholderActionItems: React.FC<StakeholderActionItemsProps> = ({ stakeholderId, type }) => {
  const [tasks, setTasks] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [assigneeFilter, setAssigneeFilter] = useState<string>('all');
  const [typeFilter, setTypeFilter] = useState<string>('all');
  const [matterFilter, setMatterFilter] = useState<string>('all');
  const [dateFilter, setDateFilter] = useState<string>('all');

  const entityLabel = isLegalEnvironment ? 'Matter' : 'Project';
  const entityDetailsPath = isLegalEnvironment ? '/matterDetails' : '/projectDetails';

  const today = useMemo(() => {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    return d;
  }, []);

  const fetchTasks = useCallback(async () => {
    setLoading(true);
    try {
      if (!stakeholderId) {
        setTasks([]);
        return;
      }

      let allMatters: any[] = [];

      if (type === 'organization') {
        // For organizations, extract the actual ID and find all clients/agents with the same company
        const actualId = stakeholderId.startsWith('org-') 
          ? stakeholderId.replace('org-', '') 
          : stakeholderId;
        
        const clients = await ClientService.getClients();
        const agents = await AgentsService.getAgents();
        
        // Find the organization's company name
        let companyName: string | null = null;
        let foundClient = clients.find((c: any) => String(c.ClientID) === actualId);
        if (foundClient && foundClient.Company) {
          companyName = foundClient.Company;
        } else {
          let foundAgent = agents.find((a: any) => String(a.AgentID) === actualId);
          if (foundAgent && foundAgent.Company) {
            companyName = foundAgent.Company;
          }
        }

        if (companyName) {
          // Get all client IDs with this company name (as strings for API)
          const orgClientIds = clients
            .filter((c: any) => c.Company === companyName)
            .map((c: any) => String(c.ClientID));
          
          // Fetch matters for all clients in the organization
          const mattersPromises = orgClientIds.map(async (clientId: string) => {
            try {
              return await ClientMattersService.getClientMatters(clientId);
            } catch (error) {
              console.error(`Error fetching matters for client ${clientId}:`, error);
              return [];
            }
          });
          
          const mattersArrays = await Promise.all(mattersPromises);
          allMatters = mattersArrays.flat();
        }
      } else {
        // For individuals, use the stakeholderId directly
        const responseData = await ClientMattersService.getClientMatters(stakeholderId);
        allMatters = Array.isArray(responseData) ? responseData : [];
      }

      // Ensure allMatters is an array before processing
      if (!Array.isArray(allMatters)) {
        allMatters = [];
      }

      // Remove duplicates
      const matters = Array.from(
        new Map(allMatters.map((m: any) => [m.matterid, m])).values()
      );
      
      const matterIds = Array.isArray(matters)
        ? matters.map((m: any) => m.matterid).filter(Boolean)
        : [];

      if (matterIds.length === 0) {
        setTasks([]);
        return;
      }

      const tasksByMatter = await Promise.all(
        matterIds.map(async (matterId) => {
          try {
            const matterTasks = await TaskService.getTasksByMatterId(matterId);
            if (!Array.isArray(matterTasks)) return [];
            // attach matterid and mattername if present on matter record
            const matterMeta = (matters || []).find((m: any) => m.matterid === matterId);
            const matterName = matterMeta?.mattername || matterMeta?.matterName || '';
            return matterTasks.map((t: any) => ({
              ...t,
              matterid: t.matterid || matterId,
              mattername: t.mattername || matterName,
            }));
          } catch (error) {
            console.error(`Error fetching tasks for matter ${matterId}:`, error);
            return [];
          }
        })
      );

      // flatten and dedupe by taskid
      const flattened = tasksByMatter.flat();
      const dedupedMap = new Map<string, any>();
      flattened.forEach((t) => {
        const key = t.taskid || `${t.task_title}-${t.matterid}`;
        if (!dedupedMap.has(key)) {
          dedupedMap.set(key, t);
        }
      });

      setTasks(Array.from(dedupedMap.values()));
    } catch (error) {
      console.error('Error fetching stakeholder tasks:', error);
      setTasks([]);
    } finally {
      setLoading(false);
    }
  }, [stakeholderId]);

  useEffect(() => {
    fetchTasks();
  }, [fetchTasks]);

  const truncateByWords = useCallback((text: string | undefined, maxWords: number) => {
    if (!text) return '';
    const words = text.trim().split(/\s+/);
    if (words.length <= maxWords) return text;
    return `${words.slice(0, maxWords).join(' ')} ...`;
  }, []);

  const formatDueDate = (dueDate?: string | null) => {
    if (!dueDate) return { label: 'N/A', tone: 'muted' as const };
    const parsed = Date.parse(dueDate);
    if (Number.isNaN(parsed)) return { label: 'N/A', tone: 'muted' as const };
    const date = new Date(parsed);
    const formatted = date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
    const diffDays = Math.ceil((date.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
    if (diffDays < 0) return { label: formatted, tone: 'danger' as const };
    if (diffDays === 0) return { label: `${formatted} (today)`, tone: 'warning' as const };
    if (diffDays <= 7) return { label: `${formatted} (${diffDays}d left)`, tone: 'warning' as const };
    return { label: formatted, tone: 'neutral' as const };
  };

  const statusVariant = (status?: string) => {
    const s = (status || '').toLowerCase();
    if (s.includes('complete')) return 'completed';
    if (s.includes('progress')) return 'in-review';
    if (s.includes('risk') || s.includes('overdue')) return 'at-risk';
    if (s.includes('pending')) return 'open';
    return 'active';
  };

  const matchesDateFilter = useCallback((rawDate: string | undefined, filter: string) => {
    if (filter === 'all') return true;
    if (!rawDate) return false;
    const parsed = Date.parse(rawDate);
    if (Number.isNaN(parsed)) return false;

    const date = new Date(parsed);
    date.setHours(0, 0, 0, 0);

    const now = new Date(today);

    switch (filter) {
      case 'today':
        return date.getTime() === now.getTime();
      case 'week': {
        const end = new Date(now);
        end.setDate(now.getDate() + 7);
        return date.getTime() >= now.getTime() && date.getTime() <= end.getTime();
      }
      case 'month': {
        const end = new Date(now);
        end.setMonth(now.getMonth() + 1);
        return date.getTime() >= now.getTime() && date.getTime() <= end.getTime();
      }
      case 'year': {
        const end = new Date(now);
        end.setFullYear(now.getFullYear() + 1);
        return date.getTime() >= now.getTime() && date.getTime() <= end.getTime();
      }
      case 'overdue':
        return date.getTime() < now.getTime();
      default:
        return true;
    }
  }, [today]);

  const filterOptions = useMemo(() => {
    if (!tasks || tasks.length === 0) {
      return { statuses: [], assignees: [], types: [], matters: [] };
    }

    const statuses = new Set<string>();
    const assignees = new Set<string>();
    const types = new Set<string>();
    const matters = new Map<string, string>();

    tasks.forEach((task) => {
      if (task.status) statuses.add(task.status);
      if (task.assignedto) assignees.add(task.assignedto);
      if (task.task_type) types.add(task.task_type);
      if (task.matterid) {
        matters.set(task.matterid, task.mattername || task.matterid);
      }
    });

    return {
      statuses: Array.from(statuses).sort().map((status) => ({ value: status, label: status })),
      assignees: Array.from(assignees).sort().map((assignee) => ({ value: assignee, label: assignee })),
      types: Array.from(types).sort().map((t) => ({ value: t, label: t })),
      matters: Array.from(matters.entries())
        .sort((a, b) => a[0].localeCompare(b[0], undefined, { numeric: true }))
        .map(([id, name]) => ({ value: id, label: `${id}${name ? ` • ${name}` : ''}` })),
    };
  }, [tasks]);

  const filteredTasks = useMemo(() => {
    let result = [...tasks];

    if (statusFilter !== 'all') {
      result = result.filter((task) => task.status === statusFilter);
    }
    if (assigneeFilter !== 'all') {
      result = result.filter((task) => task.assignedto === assigneeFilter);
    }
    if (typeFilter !== 'all') {
      result = result.filter((task) => task.task_type === typeFilter);
    }
    if (matterFilter !== 'all') {
      result = result.filter((task) => task.matterid === matterFilter);
    }
    if (dateFilter !== 'all') {
      result = result.filter((task) => matchesDateFilter(task.duedate, dateFilter));
    }

    return result;
  }, [tasks, statusFilter, assigneeFilter, typeFilter, matterFilter, dateFilter, matchesDateFilter]);

  const summary = useMemo(() => {
    if (!filteredTasks || filteredTasks.length === 0) {
      return { total: 0, inProgress: 0, overdue: 0, completed: 0 };
    }

    return filteredTasks.reduce(
      (acc, task) => {
        const status = (task.status || '').toLowerCase();
        const completed = status.includes('complete');
        const inProgress = status.includes('progress');

        let isOverdue = false;
        if (!completed && task.duedate) {
          const parsed = Date.parse(task.duedate);
          if (!Number.isNaN(parsed)) {
            const due = new Date(parsed);
            due.setHours(0, 0, 0, 0);
            isOverdue = due.getTime() < today.getTime();
          }
        }

        if (completed) acc.completed += 1;
        else if (isOverdue) acc.overdue += 1;
        else if (inProgress) acc.inProgress += 1;
        else acc.inProgress += 1; // treat pending/others as in progress bucket

        acc.total += 1;
        return acc;
      },
      { total: 0, inProgress: 0, overdue: 0, completed: 0 }
    );
  }, [filteredTasks, today]);

  const tasksColumns: Column<any>[] = [
    {
      header: 'Task Title',
      render: (task: any) => (
        <Link
          to={`${entityDetailsPath}/${task.matterid}`}
          className="stakeholder-task-link"
        >
          {task.task_title || 'Untitled Task'}
        </Link>
      ),
    },
    {
      header: entityLabel,
      render: (task: any) => (
        <Link
          to={`${entityDetailsPath}/${task.matterid}`}
          className="matterLink"
          style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}
        >
          {task.matterid}
          {task.mattername ? (
            <span className="matter-name-trunc">
              {truncateByWords(task.mattername, 7)}
            </span>
          ) : null}
          <FaExternalLinkAlt style={{ fontSize: '0.8rem', opacity: 0.7 }} />
        </Link>
      ),
    },
    {
      header: 'Status',
      render: (task: any) => (
        <StatusBadge status={task.status || 'Unknown'} variant={statusVariant(task.status)} size="small" />
      ),
    },
    {
      header: 'Type',
      accessor: 'task_type',
    },
    {
      header: 'Assigned To',
      accessor: 'assignedto',
    },
    {
      header: 'Due Date',
      render: (task: any) => {
        const due = formatDueDate(task.duedate);
        const toneClass =
          due.tone === 'danger'
            ? 'tone-danger'
            : due.tone === 'warning'
            ? 'tone-warning'
            : due.tone === 'neutral'
            ? 'tone-neutral'
            : 'tone-muted';
        return <span className={`action-due ${toneClass}`}>{due.label}</span>;
      },
    },
  ];

  return (
    <div className="StakeholderActionItems">
      
      <div className="stakeholder-summary-grid">
        <div className="stakeholder-summary-card">
          <div className="stakeholder-summary-top">
            <div className="stakeholder-summary-label">Total Items</div>
            <div className="stakeholder-summary-icon icon-muted">
              <FaListUl />
            </div>
          </div>
          <div className="stakeholder-summary-value">{summary.total}</div>
        </div>
        <div className="stakeholder-summary-card">
          <div className="stakeholder-summary-top">
            <div className="stakeholder-summary-label">In Progress</div>
            <div className="stakeholder-summary-icon icon-blue">
              <FaCircleNotch />
            </div>
          </div>
          <div className="stakeholder-summary-value summary-blue">{summary.inProgress}</div>
        </div>
        <div className="stakeholder-summary-card">
          <div className="stakeholder-summary-top">
            <div className="stakeholder-summary-label">Overdue</div>
            <div className="stakeholder-summary-icon icon-red">
              <FaExclamationTriangle />
            </div>
          </div>
          <div className="stakeholder-summary-value summary-red">{summary.overdue}</div>
        </div>
        <div className="stakeholder-summary-card">
          <div className="stakeholder-summary-top">
            <div className="stakeholder-summary-label">Completed</div>
            <div className="stakeholder-summary-icon icon-green">
              <FaCheckCircle />
            </div>
          </div>
          <div className="stakeholder-summary-value summary-green">{summary.completed}</div>
        </div>
      </div>
      <div className="stakeholder-action-filters-row">
        <FilterDropdown
          label=""
          placeholder="All Statuses"
          options={[{ value: 'all', label: 'All Statuses' }, ...filterOptions.statuses]}
          value={statusFilter}
          onChange={(value) => setStatusFilter(Array.isArray(value) ? value[0] : value)}
          className="stakeholder-action-filter-dropdown"
        />
        <FilterDropdown
          label=""
          placeholder="All Assignees"
          options={[{ value: 'all', label: 'All Assignees' }, ...filterOptions.assignees]}
          value={assigneeFilter}
          onChange={(value) => setAssigneeFilter(Array.isArray(value) ? value[0] : value)}
          className="stakeholder-action-filter-dropdown"
        />
        <FilterDropdown
          label=""
          placeholder="All Types"
          options={[{ value: 'all', label: 'All Types' }, ...filterOptions.types]}
          value={typeFilter}
          onChange={(value) => setTypeFilter(Array.isArray(value) ? value[0] : value)}
          className="stakeholder-action-filter-dropdown"
        />
        <FilterDropdown
          label=""
          placeholder="All Matters"
          options={[{ value: 'all', label: 'All Matters' }, ...filterOptions.matters]}
          value={matterFilter}
          onChange={(value) => setMatterFilter(Array.isArray(value) ? value[0] : value)}
          className="stakeholder-action-filter-dropdown"
        />
        <FilterDropdown
          label=""
          placeholder="All Dates"
          options={[
            { value: 'all', label: 'All Dates' },
            { value: 'today', label: 'Today' },
            { value: 'week', label: 'This Week' },
            { value: 'month', label: 'This Month' },
            { value: 'year', label: 'This Year' },
            { value: 'overdue', label: 'Overdue' },
          ]}
          value={dateFilter}
          onChange={(value) => setDateFilter(Array.isArray(value) ? value[0] : value)}
          className="stakeholder-action-filter-dropdown"
        />
      </div>
      {loading ? (
        <div>Loading...</div>
      ) : (
        <Table
          columns={tasksColumns}
          data={filteredTasks}
          rowKey="taskid"
          emptyMessage={`No action items found for this ${type === 'organization' ? 'organization' : 'individual'}`}
        />
      )}
    </div>
  );
};

export default StakeholderActionItems;

